import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  Building2,
  ClipboardCheck,
  Download,
  FileText,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  Users,
  Wallet,
} from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { ApiError } from "@/lib/api/client";
import {
  createInvoice,
  deleteInvoice,
  downloadClinicForm,
  getClinic,
  getClinicBilling,
  listClinicAudit,
  listClinicExams,
  listClinicForms,
  listClinicPatients,
  listClinicUsers,
  payInvoice,
  toggleClinicBlock,
  updateBillingPlan,
} from "@/lib/api/admin";
import { formatDate, formatDateTime } from "@/lib/format";

const STATUS_COLOR: Record<string, string> = {
  trial: "yellow",
  active: "green",
  overdue: "orange",
  blocked: "red",
  cancelled: "gray",
};

const STATUS_LABEL: Record<string, string> = {
  trial: "Sinov",
  active: "Faol",
  overdue: "Muddati o'tgan",
  blocked: "Bloklangan",
  cancelled: "Bekor qilingan",
};

export function AdminClinicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: clinic, isLoading } = useQuery({
    queryKey: ["admin-clinic", id],
    queryFn: () => getClinic(id!),
    enabled: !!id,
  });

  const blockMutation = useMutation({
    mutationFn: () => toggleClinicBlock(id!),
    onSuccess: (plan) => {
      queryClient.invalidateQueries({ queryKey: ["admin-clinic", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-clinic-billing", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-clinics"] });
      notifications.show({
        color: plan.status === "blocked" ? "red" : "green",
        message:
          plan.status === "blocked"
            ? "Klinika bloklandi"
            : "Klinika faollashtirildi",
      });
    },
  });

  if (isLoading || !clinic) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title={clinic.name}
        subtitle={clinic.legal_name ?? undefined}
        back
        actions={
          <Group gap="sm">
            {clinic.billing_status && (
              <Badge
                variant="light"
                color={STATUS_COLOR[clinic.billing_status] ?? "gray"}
                radius="sm"
                size="lg"
              >
                {STATUS_LABEL[clinic.billing_status] ?? clinic.billing_status}
              </Badge>
            )}
            <Button
              variant={clinic.billing_status === "blocked" ? "filled" : "light"}
              color={clinic.billing_status === "blocked" ? "green" : "red"}
              leftSection={
                clinic.billing_status === "blocked" ? (
                  <ShieldCheck size={16} strokeWidth={2.2} />
                ) : (
                  <ShieldAlert size={16} strokeWidth={2.2} />
                )
              }
              loading={blockMutation.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    clinic.billing_status === "blocked"
                      ? "Klinikani faollashtirasizmi?"
                      : "Klinikani bloklaysizmi? Barcha xodimlar kira olmaydi.",
                  )
                ) {
                  blockMutation.mutate();
                }
              }}
            >
              {clinic.billing_status === "blocked" ? "Faollashtirish" : "Bloklash"}
            </Button>
          </Group>
        }
      />

      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <StatTile
          icon={<Users size={20} strokeWidth={2} />}
          color="violet"
          label="Xodimlar"
          value={clinic.user_count}
        />
        <StatTile
          icon={<Activity size={20} strokeWidth={2} />}
          color="blue"
          label="Bemorlar"
          value={clinic.patient_count}
        />
        <StatTile
          icon={<ClipboardCheck size={20} strokeWidth={2} />}
          color="indigo"
          label="Ko'riklar"
          value={clinic.examination_count}
        />
        <StatTile
          icon={<ShieldCheck size={20} strokeWidth={2} />}
          color="green"
          label="Yakunlangan"
          value={clinic.completed_exam_count}
        />
      </SimpleGrid>

      <Tabs defaultValue="overview" variant="pills" radius="md">
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<Building2 size={14} />}>
            Umumiy
          </Tabs.Tab>
          <Tabs.Tab value="users" leftSection={<Users size={14} />}>
            Xodimlar
          </Tabs.Tab>
          <Tabs.Tab value="patients" leftSection={<Activity size={14} />}>
            Bemorlar
          </Tabs.Tab>
          <Tabs.Tab value="exams" leftSection={<ClipboardCheck size={14} />}>
            Ko'riklar
          </Tabs.Tab>
          <Tabs.Tab value="forms" leftSection={<FileText size={14} />}>
            Hujjatlar
          </Tabs.Tab>
          <Tabs.Tab value="audit" leftSection={<ScrollText size={14} />}>
            Audit
          </Tabs.Tab>
          <Tabs.Tab value="billing" leftSection={<Wallet size={14} />}>
            Billing
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="lg">
          <OverviewTab clinic={clinic} />
        </Tabs.Panel>
        <Tabs.Panel value="users" pt="lg"><UsersTab clinicId={id!} /></Tabs.Panel>
        <Tabs.Panel value="patients" pt="lg"><PatientsTab clinicId={id!} /></Tabs.Panel>
        <Tabs.Panel value="exams" pt="lg"><ExamsTab clinicId={id!} /></Tabs.Panel>
        <Tabs.Panel value="forms" pt="lg"><FormsTab clinicId={id!} /></Tabs.Panel>
        <Tabs.Panel value="audit" pt="lg"><AuditTab clinicId={id!} /></Tabs.Panel>
        <Tabs.Panel value="billing" pt="lg"><BillingTab clinicId={id!} /></Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────
function OverviewTab({ clinic }: { clinic: any }) {
  const Row = ({ label, value }: { label: string; value: string | null | undefined }) => (
    <Group justify="space-between" wrap="nowrap" py={6} style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}>
      <Text size="sm" c="dimmed">{label}</Text>
      <Text size="sm" fw={600}>{value ?? "—"}</Text>
    </Group>
  );
  return (
    <Card padding="lg" radius="xl">
      <Stack gap={0}>
        <Row label="Yuridik nomi" value={clinic.legal_name} />
        <Row label="Litsenziya" value={clinic.license_number} />
        <Row label="STIR (INN)" value={clinic.inn} />
        <Row label="Manzil" value={clinic.address} />
        <Row label="Telefon" value={clinic.phone} />
        <Row label="Yaratilgan" value={formatDateTime(clinic.created_at)} />
        <Row label="Oxirgi ko'rik" value={clinic.last_exam_at ? formatDateTime(clinic.last_exam_at) : "—"} />
        <Row label="Trial muddati" value={clinic.trial_until ? formatDate(clinic.trial_until) : "—"} />
        <Row label="To'langan muddati" value={clinic.paid_until ? formatDate(clinic.paid_until) : "—"} />
        <Row label="Oylik to'lov" value={clinic.monthly_fee ? `${clinic.monthly_fee.toLocaleString()} UZS` : "—"} />
      </Stack>
    </Card>
  );
}

// ─── Users ───────────────────────────────────────────────────────────────
function UsersTab({ clinicId }: { clinicId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-clinic-users", clinicId],
    queryFn: () => listClinicUsers(clinicId),
  });
  return (
    <Card padding={0} radius="xl">
      <Table.ScrollContainer minWidth={780}>
        <Table verticalSpacing="md" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>F.I.SH</Table.Th>
              <Table.Th>Login</Table.Th>
              <Table.Th>Roli</Table.Th>
              <Table.Th>Mutaxassisligi</Table.Th>
              <Table.Th>Holati</Table.Th>
              <Table.Th>Oxirgi kirish</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && <Table.Tr><Table.Td colSpan={6}><Loader size="sm" /></Table.Td></Table.Tr>}
            {(data ?? []).map((u) => (
              <Table.Tr key={u.id}>
                <Table.Td><Text size="sm" fw={600}>{u.full_name}</Text></Table.Td>
                <Table.Td><Text size="sm" ff="monospace">{u.email}</Text></Table.Td>
                <Table.Td><Badge variant="light" radius="sm">{u.role}</Badge></Table.Td>
                <Table.Td><Text size="sm">{u.specialty ?? "—"}</Text></Table.Td>
                <Table.Td>
                  <Badge variant="light" color={u.is_active ? "green" : "gray"} radius="sm">
                    {u.is_active ? "Faol" : "Nofaol"}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {u.last_login_at ? formatDateTime(u.last_login_at) : "—"}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}

// ─── Patients ────────────────────────────────────────────────────────────
function PatientsTab({ clinicId }: { clinicId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-clinic-patients", clinicId],
    queryFn: () => listClinicPatients(clinicId),
  });
  return (
    <Card padding={0} radius="xl">
      <Table.ScrollContainer minWidth={780}>
        <Table verticalSpacing="md" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Karta</Table.Th>
              <Table.Th>F.I.SH</Table.Th>
              <Table.Th>Tug'ilgan sana</Table.Th>
              <Table.Th>Jinsi</Table.Th>
              <Table.Th>Ish joyi</Table.Th>
              <Table.Th>Lavozim</Table.Th>
              <Table.Th>Yaratilgan</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && <Table.Tr><Table.Td colSpan={7}><Loader size="sm" /></Table.Td></Table.Tr>}
            {(data ?? []).map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td><Text size="sm" ff="monospace" fw={600}>{p.card_number}</Text></Table.Td>
                <Table.Td><Text size="sm" fw={600}>{p.family_name} {p.given_name}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{p.birth_date ?? "—"}</Text></Table.Td>
                <Table.Td><Text size="sm">{p.sex ?? "—"}</Text></Table.Td>
                <Table.Td><Text size="sm">{p.workplace ?? "—"}</Text></Table.Td>
                <Table.Td><Text size="sm">{p.position ?? "—"}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{formatDate(p.created_at)}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}

// ─── Exams ───────────────────────────────────────────────────────────────
function ExamsTab({ clinicId }: { clinicId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-clinic-exams", clinicId],
    queryFn: () => listClinicExams(clinicId),
  });
  return (
    <Card padding={0} radius="xl">
      <Table.ScrollContainer minWidth={900}>
        <Table verticalSpacing="md" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>#</Table.Th>
              <Table.Th>Bemor</Table.Th>
              <Table.Th>Ish joyi</Table.Th>
              <Table.Th>Turi</Table.Th>
              <Table.Th>Holat</Table.Th>
              <Table.Th>Xulosa</Table.Th>
              <Table.Th>Sana</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && <Table.Tr><Table.Td colSpan={7}><Loader size="sm" /></Table.Td></Table.Tr>}
            {(data ?? []).map((e) => (
              <Table.Tr key={e.id}>
                <Table.Td><Text size="sm" ff="monospace" c="brand.7" fw={700}>#{e.exam_number}</Text></Table.Td>
                <Table.Td>
                  <Stack gap={0}>
                    <Text size="sm" fw={600}>{e.patient_name}</Text>
                    <Text size="xs" c="dimmed" ff="monospace">{e.patient_card}</Text>
                  </Stack>
                </Table.Td>
                <Table.Td><Text size="sm">{e.job_title ?? "—"}</Text></Table.Td>
                <Table.Td><Badge variant="light" radius="sm">{e.type}</Badge></Table.Td>
                <Table.Td>
                  <Badge
                    variant="light"
                    color={e.status === "completed" ? "green" : e.status === "cancelled" ? "red" : "blue"}
                    radius="sm"
                  >
                    {e.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {e.final_verdict ? (
                    <Badge variant="filled" color="green" radius="sm">{e.final_verdict}</Badge>
                  ) : <Text size="xs" c="dimmed">—</Text>}
                </Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{formatDate(e.created_at)}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}

// ─── Forms (PDF download) ───────────────────────────────────────────────
function FormsTab({ clinicId }: { clinicId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-clinic-forms", clinicId],
    queryFn: () => listClinicForms(clinicId),
  });

  const downloadOne = async (formId: string, formNumber: string) => {
    try {
      const blob = await downloadClinicForm(clinicId, formId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${formNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      notifications.show({ color: "red", message: "PDF yuklab bo'lmadi" });
    }
  };

  return (
    <Card padding={0} radius="xl">
      <Table.ScrollContainer minWidth={780}>
        <Table verticalSpacing="md" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Hujjat raqami</Table.Th>
              <Table.Th>Bemor</Table.Th>
              <Table.Th>Forma turi</Table.Th>
              <Table.Th>Holat</Table.Th>
              <Table.Th>Yaratilgan</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && <Table.Tr><Table.Td colSpan={6}><Loader size="sm" /></Table.Td></Table.Tr>}
            {(data ?? []).map((f) => (
              <Table.Tr key={f.id}>
                <Table.Td><Text size="sm" fw={700} ff="monospace" c="brand.7">{f.form_number}</Text></Table.Td>
                <Table.Td>
                  <Stack gap={0}>
                    <Text size="sm" fw={600}>{f.patient_name}</Text>
                    <Text size="xs" c="dimmed" ff="monospace">{f.patient_card}</Text>
                  </Stack>
                </Table.Td>
                <Table.Td><Badge variant="light" radius="sm">{f.template}</Badge></Table.Td>
                <Table.Td>
                  {f.revoked_at ? (
                    <Badge variant="light" color="red" radius="sm">Bekor qilingan</Badge>
                  ) : (
                    <Badge variant="light" color="green" radius="sm">Faol</Badge>
                  )}
                </Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{formatDateTime(f.generated_at)}</Text></Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    variant="light"
                    radius="xl"
                    leftSection={<Download size={14} strokeWidth={2.2} />}
                    onClick={() => downloadOne(f.id, f.form_number)}
                  >
                    Yuklab olish
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}

// ─── Audit ───────────────────────────────────────────────────────────────
function AuditTab({ clinicId }: { clinicId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-clinic-audit", clinicId],
    queryFn: () => listClinicAudit(clinicId),
  });
  return (
    <Card padding={0} radius="xl">
      <Table.ScrollContainer minWidth={900}>
        <Table verticalSpacing="md" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Vaqt</Table.Th>
              <Table.Th>Foydalanuvchi roli</Table.Th>
              <Table.Th>Harakat</Table.Th>
              <Table.Th>Tur</Table.Th>
              <Table.Th>Obyekt</Table.Th>
              <Table.Th>O'zgarishlar</Table.Th>
              <Table.Th>IP</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && <Table.Tr><Table.Td colSpan={7}><Loader size="sm" /></Table.Td></Table.Tr>}
            {(data ?? []).map((a) => (
              <Table.Tr key={a.id}>
                <Table.Td><Text size="xs" c="dimmed">{formatDateTime(a.created_at)}</Text></Table.Td>
                <Table.Td><Text size="xs">{a.actor_role ?? "—"}</Text></Table.Td>
                <Table.Td><Text size="xs" ff="monospace">{a.permission}</Text></Table.Td>
                <Table.Td>
                  <Badge variant="light" size="xs" radius="sm">{a.verb}</Badge>
                </Table.Td>
                <Table.Td><Text size="xs">{a.target_type ?? "—"}</Text></Table.Td>
                <Table.Td>
                  {a.diff_keys && a.diff_keys.length > 0 ? (
                    <Group gap={4}>
                      {a.diff_keys.slice(0, 3).map((k) => (
                        <Badge key={k} size="xs" variant="light" radius="sm">{k}</Badge>
                      ))}
                      {a.diff_keys.length > 3 && (
                        <Text size="xs" c="dimmed">+{a.diff_keys.length - 3}</Text>
                      )}
                    </Group>
                  ) : "—"}
                </Table.Td>
                <Table.Td><Text size="xs" ff="monospace" c="dimmed">{a.ip_address ?? "—"}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}

// ─── Billing ─────────────────────────────────────────────────────────────
function BillingTab({ clinicId }: { clinicId: string }) {
  const queryClient = useQueryClient();
  const [invoiceOpen, invoiceHandlers] = useDisclosure(false);
  const [payOpen, payHandlers] = useDisclosure(false);
  const [paying, setPaying] = useState<{ id: string; amount: number } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-clinic-billing", clinicId],
    queryFn: () => getClinicBilling(clinicId),
  });

  const planForm = useForm({
    initialValues: { monthly_fee: 0, status: "trial" as string },
  });

  const planMutation = useMutation({
    mutationFn: (v: { monthly_fee?: number; status?: string }) =>
      updateBillingPlan(clinicId, v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-clinic-billing", clinicId] });
      queryClient.invalidateQueries({ queryKey: ["admin-clinic", clinicId] });
      notifications.show({ color: "green", message: "Reja yangilandi" });
    },
  });

  const invoiceForm = useForm({
    initialValues: {
      period_from: new Date().toISOString().slice(0, 10),
      period_to: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
        .toISOString().slice(0, 10),
      amount: 500000,
      notes: "",
    },
    validate: {
      amount: (v) => (v >= 0 ? null : "Summa noto'g'ri"),
    },
  });

  const createInvoiceMutation = useMutation({
    mutationFn: (v: typeof invoiceForm.values) => createInvoice(clinicId, v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-clinic-billing", clinicId] });
      notifications.show({ color: "green", message: "Invoys yaratildi" });
      invoiceForm.reset();
      invoiceHandlers.close();
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : "Invoys yaratib bo'lmadi";
      notifications.show({ color: "red", message: msg });
    },
  });

  const payForm = useForm({
    initialValues: { amount: 0, method: "cash", notes: "" },
  });

  const payMutation = useMutation({
    mutationFn: (v: typeof payForm.values) =>
      payInvoice(clinicId, paying!.id, v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-clinic-billing", clinicId] });
      queryClient.invalidateQueries({ queryKey: ["admin-clinic", clinicId] });
      notifications.show({ color: "green", message: "To'lov belgilandi" });
      payHandlers.close();
      setPaying(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (invId: string) => deleteInvoice(clinicId, invId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-clinic-billing", clinicId] });
      notifications.show({ color: "green", message: "Invoys o'chirildi" });
    },
  });

  if (isLoading || !data) return <Loader />;
  const { plan, invoices } = data;

  return (
    <Stack gap="lg">
      <Card padding="lg" radius="xl">
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <ThemeIcon size={36} radius="xl" variant="light" color="brand">
              <Wallet size={18} strokeWidth={2} />
            </ThemeIcon>
            <Title order={5} fw={700}>To'lov rejasi</Title>
          </Group>
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Reja</Text>
            <Text fw={700}>{plan.plan_name}</Text>
          </Stack>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Oylik to'lov</Text>
            <Text fw={700}>{Number(plan.monthly_fee).toLocaleString()} {plan.currency}</Text>
          </Stack>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Holat</Text>
            <Badge
              variant="light"
              color={STATUS_COLOR[plan.status] ?? "gray"}
              radius="sm"
              size="lg"
              style={{ width: "fit-content" }}
            >
              {STATUS_LABEL[plan.status] ?? plan.status}
            </Badge>
          </Stack>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Sinov muddati</Text>
            <Text size="sm">{plan.trial_until ? formatDate(plan.trial_until) : "—"}</Text>
          </Stack>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>To'langan muddati</Text>
            <Text size="sm">{plan.paid_until ? formatDate(plan.paid_until) : "—"}</Text>
          </Stack>
          <Group gap="xs" align="flex-end">
            <NumberInput
              label="Yangi oylik to'lov"
              size="xs"
              thousandSeparator=" "
              min={0}
              defaultValue={Number(plan.monthly_fee)}
              {...planForm.getInputProps("monthly_fee")}
            />
            <Button
              size="xs"
              radius="xl"
              loading={planMutation.isPending}
              onClick={() =>
                planMutation.mutate({ monthly_fee: planForm.values.monthly_fee })
              }
            >
              Saqlash
            </Button>
          </Group>
        </SimpleGrid>
      </Card>

      <Card padding={0} radius="xl">
        <Group p="lg" pb="md" justify="space-between">
          <Group gap="sm">
            <ThemeIcon size={36} radius="xl" variant="light" color="orange">
              <AlertCircle size={18} strokeWidth={2} />
            </ThemeIcon>
            <Title order={5} fw={700}>Invoyslar</Title>
          </Group>
          <Button
            radius="xl"
            size="sm"
            onClick={invoiceHandlers.open}
          >
            Yangi invoys
          </Button>
        </Group>
        <Table.ScrollContainer minWidth={780}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Davr</Table.Th>
                <Table.Th>Summa</Table.Th>
                <Table.Th>To'langan</Table.Th>
                <Table.Th>Holat</Table.Th>
                <Table.Th>To'lov sanasi</Table.Th>
                <Table.Th>Usul</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {invoices.length === 0 && (
                <Table.Tr><Table.Td colSpan={7}>
                  <Text c="dimmed" ta="center" py="md">Hali invoys yo'q</Text>
                </Table.Td></Table.Tr>
              )}
              {invoices.map((i) => (
                <Table.Tr key={i.id}>
                  <Table.Td>
                    <Text size="sm">{formatDate(i.period_from)} – {formatDate(i.period_to)}</Text>
                  </Table.Td>
                  <Table.Td><Text fw={600}>{Number(i.amount).toLocaleString()} {i.currency}</Text></Table.Td>
                  <Table.Td><Text>{Number(i.paid_amount).toLocaleString()} {i.currency}</Text></Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={i.status === "paid" ? "green" : i.status === "partial" ? "yellow" : i.status === "overdue" ? "red" : "blue"}
                      radius="sm"
                    >
                      {i.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{i.paid_at ? formatDateTime(i.paid_at) : "—"}</Text></Table.Td>
                  <Table.Td><Text size="xs">{i.method ?? "—"}</Text></Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {i.status !== "paid" && (
                        <Button
                          size="xs"
                          variant="light"
                          color="green"
                          onClick={() => {
                            setPaying({ id: i.id, amount: Number(i.amount) - Number(i.paid_amount) });
                            payForm.setValues({
                              amount: Number(i.amount) - Number(i.paid_amount),
                              method: "cash",
                              notes: "",
                            });
                            payHandlers.open();
                          }}
                        >
                          To'lash
                        </Button>
                      )}
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => {
                          if (window.confirm("Invoysni o'chirasizmi?")) {
                            deleteMutation.mutate(i.id);
                          }
                        }}
                      >
                        <Trash2 size={14} strokeWidth={2} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Modal
        opened={invoiceOpen}
        onClose={invoiceHandlers.close}
        title="Yangi invoys"
        radius="lg"
      >
        <form onSubmit={invoiceForm.onSubmit((v) => createInvoiceMutation.mutate(v))}>
          <Stack>
            <SimpleGrid cols={2}>
              <TextInput
                label="Davr boshi"
                type="date"
                {...invoiceForm.getInputProps("period_from")}
              />
              <TextInput
                label="Davr oxiri"
                type="date"
                {...invoiceForm.getInputProps("period_to")}
              />
            </SimpleGrid>
            <NumberInput
              label="Summa (UZS)"
              thousandSeparator=" "
              min={0}
              {...invoiceForm.getInputProps("amount")}
            />
            <Textarea
              label="Izoh"
              {...invoiceForm.getInputProps("notes")}
            />
            <Group justify="flex-end">
              <Button variant="subtle" color="gray" onClick={invoiceHandlers.close}>
                Bekor qilish
              </Button>
              <Button type="submit" loading={createInvoiceMutation.isPending}>
                Yaratish
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={payOpen}
        onClose={() => { payHandlers.close(); setPaying(null); }}
        title="To'lovni belgilash"
        radius="lg"
      >
        <form onSubmit={payForm.onSubmit((v) => payMutation.mutate(v))}>
          <Stack>
            <NumberInput
              label="Summa"
              thousandSeparator=" "
              min={0}
              {...payForm.getInputProps("amount")}
            />
            <Select
              label="To'lov usuli"
              data={[
                { value: "cash", label: "Naqd" },
                { value: "card", label: "Karta" },
                { value: "bank_transfer", label: "Bank o'tkazma" },
              ]}
              {...payForm.getInputProps("method")}
            />
            <Textarea label="Izoh" {...payForm.getInputProps("notes")} />
            <Group justify="flex-end">
              <Button variant="subtle" color="gray" onClick={payHandlers.close}>
                Bekor qilish
              </Button>
              <Button type="submit" color="green" loading={payMutation.isPending}>
                To'lovni belgilash
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}

// ─── shared bits ─────────────────────────────────────────────────────────
function StatTile({ icon, color, label, value }: {
  icon: React.ReactNode; color: string; label: string; value: number | string;
}) {
  return (
    <Card padding="md" radius="xl">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon size={36} radius="xl" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Stack gap={2}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: "0.04em" }}>
            {label}
          </Text>
          <Text size="xl" fw={800} lh={1}>{value}</Text>
        </Stack>
      </Group>
    </Card>
  );
}
